import React, { useEffect, useState } from 'react';
import { Modal, View } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import { dispatchService } from '../../../services/central-api/dispatch';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';
import ActingBodiesUnitsDropdown from '../../../components/Dropdowns/ActingBodiesUnitsDropdown';

export default function UnitFinishEarlyReasonsModal({ onClose, unitFinishEarlyReason, isModalVisible, authenticatedUser }: { onClose: () => void; unitFinishEarlyReason?: UnitFinishEarlyReason; isModalVisible: boolean; authenticatedUser?: AuthenticatedUser | null; }) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [name, setName] = useState(unitFinishEarlyReason?.name || '');
    const [actingBodyId, setActingBodyId] = useState<number>(0);
    const [actingBodyUnitId, setActingBodyUnitId] = useState<number>(0);

    function isNameInvalid(value: string) {
        return value == '';
    }

    function isFormValid() {
        return !isNameInvalid(name);
    }

    function clearValues() {
        setName('');
        setActingBodyId(0);
        setActingBodyUnitId(0);
    }

    async function loadPageInfo() {
        if (!authenticatedUser) {
            return;
        }
        if (unitFinishEarlyReason) {
            setName(unitFinishEarlyReason.name);
            setActingBodyId(unitFinishEarlyReason.actingBody.id ?? 0);
            setActingBodyUnitId(unitFinishEarlyReason.actingBodyUnitId ?? 0);
        } else {
            if (!authenticatedUser.permissions.dispatch_manage_all_acting_bodies_unit_finish_early_reasons &&
                authenticatedUser.permissions.dispatch_manage_acting_body_units_unit_finish_early_reasons
            ) {
                setActingBodyId(authenticatedUser?.actingBodyUnit.actingBodyId ?? 0);
            } else if (!authenticatedUser.permissions.dispatch_manage_all_acting_bodies_unit_finish_early_reasons &&
                !authenticatedUser.permissions.dispatch_manage_acting_body_units_unit_finish_early_reasons) {
                setActingBodyId(authenticatedUser?.actingBodyUnit.actingBodyId ?? 0);
                setActingBodyUnitId(authenticatedUser?.actingBodyUnitId ?? 0);
            }
        }
    }

    useEffect(() => {
        if (isModalVisible) {
            loadPageInfo();
        }
    }, [isModalVisible]);

    async function formSubmit() {
        try {
            if (actingBodyId === 0) {
                return;
            }

            if (unitFinishEarlyReason?.id) {
                await dispatchService.updateUnitFinishEarlyReason({
                    id: unitFinishEarlyReason.id,
                    actingBodyUnitId: actingBodyUnitId != 0 ? actingBodyUnitId : undefined,
                    name: name,
                });
            } else {
                await dispatchService.createUnitFinishEarlyReason({
                    name: name,
                    actingBodyUnitId: actingBodyUnitId !== 0 ? actingBodyUnitId : undefined,
                    actingBodyId: actingBodyId,
                });
            }

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            onClose();
            clearValues();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Modal
            transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                onClose();
                clearValues();
            }}>
            <View style={styles.modalContainer}>
                <View style={styles.centeredView}>
                    <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: '20%' }]}>
                        <ActingBodiesDropdown
                            includeAllOption={false}
                            value={actingBodyId}
                            setValue={setActingBodyId}
                            zIndex={5}
                            disabled={!!unitFinishEarlyReason?.id || !authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_unit_finish_early_reasons}
                        />
                        <ActingBodiesUnitsDropdown
                            value={actingBodyUnitId}
                            actingBodyId={actingBodyId}
                            setValue={setActingBodyUnitId}
                            disabled={!(authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_unit_finish_early_reasons
                                || authenticatedUser?.permissions.dispatch_manage_acting_body_units_unit_finish_early_reasons)}
                            includeEmptyItem={true}
                            zIndex={4}
                        />
                        <FormInput
                            label={translate('name')}
                            placeholder={translate('name')}
                            value={name}
                            onChangeText={setName}
                            invalid={isNameInvalid}
                        />

                        <FormActions
                            onSubmit={formSubmit}
                            onClose={() => {
                                onClose();
                                clearValues();
                            }}
                            disabled={!isFormValid()} />
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    formContainer: {
        minWidth: 300,
        minHeight: 150,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25
    },
    modalContainer: {
        flex: 1,
        backgroundColor: '#31313199'
    },
    centeredView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    label: {
        color: theme.colors.labelColor,
        fontSize: 16
    },
    error: {
        color: '#FF0000',
        fontSize: 10
    },
}));
